import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, ReplaySubject, Subject, forkJoin, of } from 'rxjs';
import { catchError, defaultIfEmpty, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { ApiService } from '../../../services/api/api.service';
import {
  DeliveryService,
  DeliverySummary,
  NO_DELIVERY,
} from '../../../services/message/delivery.service';
import { UserProfileService } from '../../../services/user/user-profile.service';
import {
  canStillSend,
  hasNotSent,
  scheduleMoment,
} from '../../../pages/organization/campaigns/campaign-list/campaign-facts';

/** Where a campaign's recipients ended up, summed over all its sends. */
export interface CampaignOutcome extends DeliverySummary {
  /**
   * 'campaign': report rows counted by campaign uuid.
   * 'messages': summed per message, for campaigns whose rows lack the uuid.
   * 'unsent': nothing has gone out yet, so nothing was asked.
   */
  source: 'campaign' | 'messages' | 'unsent';
}

/** The soonest send still to come, and how many are queued in all. */
export interface CampaignNextSend {
  at: Date | null;
  upcoming: number;
  timeZone: string | null;
}

// Same receipt states DeliveryService treats as failed.
const FAILED_STATES = ['REJECTED', 'UNDELIVERABLE', 'EXPIRED', 'DELETED', 'SKIPPED'];
/** Receipts keep arriving and schedules move, so answers expire. */
const CACHE_MS = 60_000;
/** Rows load a few campaigns at a time instead of all at once. */
const CONCURRENCY = 3;

const NO_NEXT: CampaignNextSend = { at: null, upcoming: 0, timeZone: null };

interface Job {
  run: () => Observable<unknown>;
  result: ReplaySubject<unknown>;
  key: string;
}

/**
 * Per-campaign facts for the list that the list endpoint does not carry:
 * the delivery outcome (count queries on engagement-message-reports filtered
 * by campaignUuid) and the next scheduled send (the campaign's messages and
 * their schedules). Answers are cached for a minute and loaded a few at a
 * time.
 */
@Injectable({ providedIn: 'root' })
export class CampaignInsightsService {
  private api = inject(ApiService);
  private delivery = inject(DeliveryService);
  private user = inject(UserProfileService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private cache = new Map<string, { at: number; result: ReplaySubject<unknown> }>();
  private queue = new Subject<Job>();

  constructor() {
    this.queue
      .pipe(
        mergeMap(
          (job) =>
            job.run().pipe(
              tap((value) => {
                job.result.next(value);
                job.result.complete();
              }),
              catchError((error) => {
                // Let the next view retry instead of caching the failure.
                this.cache.delete(job.key);
                job.result.error(error);
                return EMPTY;
              })
            ),
          CONCURRENCY
        )
      )
      .subscribe();
  }

  outcome(campaign: any): Observable<CampaignOutcome> {
    const uuid = String(campaign?.uuid ?? '');
    if (!uuid) return of({ ...NO_DELIVERY, source: 'unsent' as const });
    if (hasNotSent(campaign?.status)) return of({ ...NO_DELIVERY, source: 'unsent' as const });
    return this.cached<CampaignOutcome>(`outcome:${uuid}`, () => this.loadOutcome(uuid, campaign));
  }

  nextSend(campaign: any): Observable<CampaignNextSend> {
    if (!canStillSend(campaign?.status)) return of(NO_NEXT);
    const key = String(campaign?.uuid ?? campaign?.id ?? '');
    if (!key) return of(NO_NEXT);
    return this.cached<CampaignNextSend>(`next:${key}`, () => this.loadNextSend(campaign));
  }

  /** Drop what is known about a campaign, e.g. after it is deleted. */
  forget(campaign: any) {
    const uuid = String(campaign?.uuid ?? '');
    const key = String(campaign?.uuid ?? campaign?.id ?? '');
    this.cache.delete(`outcome:${uuid}`);
    this.cache.delete(`next:${key}`);
  }

  private cached<T>(key: string, run: () => Observable<T>): Observable<T> {
    if (!this.isBrowser) return EMPTY;
    const hit = this.cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_MS) return hit.result.asObservable() as Observable<T>;

    const result = new ReplaySubject<unknown>(1);
    this.cache.set(key, { at: Date.now(), result });
    this.queue.next({ run, result, key });
    return result.asObservable() as Observable<T>;
  }

  // ── Outcome ──

  private loadOutcome(uuid: string, campaign: any): Observable<CampaignOutcome> {
    const count = (states?: string[]) =>
      this.api
        .get<number>('blackkonnectcxpengagement/api/engagement-message-reports/count', {
          'campaignUuid.equals': uuid,
          ...(states ? { 'deliveryStatus.in': states } : {}),
        })
        .pipe(map((value) => Number(value) || 0));

    return forkJoin({
      total: count(),
      delivered: count(['DELIVERED']),
      failed: count(FAILED_STATES),
    }).pipe(
      switchMap(({ total, delivered, failed }) => {
        if (total > 0) {
          return of<CampaignOutcome>({
            total,
            delivered,
            failed,
            inFlight: Math.max(0, total - delivered - failed),
            source: 'campaign',
          });
        }
        // Report rows written without the campaign uuid would count as
        // zero: add up the campaign's messages instead, when the list row
        // carries them.
        return this.sumMessages(campaign);
      })
    );
  }

  private sumMessages(campaign: any): Observable<CampaignOutcome> {
    const uuids: string[] = (Array.isArray(campaign?.engagementMessages) ? campaign.engagementMessages : [])
      .map((message: any) => String(message?.uuid ?? ''))
      .filter(Boolean);
    if (!uuids.length) return of({ ...NO_DELIVERY, source: 'campaign' as const });

    return forkJoin(
      uuids.map((uuid) =>
        this.delivery.summary(uuid).pipe(
          take(1),
          defaultIfEmpty(NO_DELIVERY),
          catchError(() => of(NO_DELIVERY))
        )
      )
    ).pipe(
      map((summaries) =>
        summaries.reduce<CampaignOutcome>(
          (sum, item) => ({
            total: sum.total + item.total,
            delivered: sum.delivered + item.delivered,
            failed: sum.failed + item.failed,
            inFlight: sum.inFlight + item.inFlight,
            source: 'messages',
          }),
          { ...NO_DELIVERY, source: 'messages' }
        )
      )
    );
  }

  // ── Next send ──

  private loadNextSend(campaign: any): Observable<CampaignNextSend> {
    const nested: any[] = Array.isArray(campaign?.engagementMessages) ? campaign.engagementMessages : [];

    // The list row may already carry the schedules.
    if (nested.some((message) => Array.isArray(message?.engagementMessageSchedules) && message.engagementMessageSchedules.length)) {
      return of(soonest(nested.flatMap((message) => message.engagementMessageSchedules ?? [])));
    }

    const messageIds$: Observable<number[]> = nested.some((message) => message?.id != null)
      ? of(nested.map((message) => message.id).filter((id) => id != null))
      : this.api
          .get<any>(
            `blackkonnectcxpengagement/api/engagement-campaigns/organisations/${this.user.organizationId}/${campaign.id}`
          )
          .pipe(
            map((details) =>
              (Array.isArray(details?.engagementMessages) ? details.engagementMessages : [])
                .map((message: any) => message?.id)
                .filter((id: unknown) => id != null)
            )
          );

    return messageIds$.pipe(
      switchMap((ids) => {
        if (!ids.length) return of(NO_NEXT);
        // The same per-message request the campaign page makes for schedules.
        return forkJoin(
          ids.map((id) =>
            this.api
              .get<any>(`blackkonnectcxpengagement/api/engagement-messages/${id}`)
              .pipe(catchError(() => of(null)))
          )
        ).pipe(
          map((messages) =>
            soonest(messages.flatMap((message: any) => message?.engagementMessageSchedules ?? []))
          )
        );
      })
    );
  }
}

/** Schedules that already ran, or were stopped, are not "next". */
const SETTLED = /complet|finish|fail|cancel|abandon|stopp|sent|deliver/i;

function soonest(schedules: any[]): CampaignNextSend {
  const now = Date.now();
  const upcoming = schedules
    .filter((schedule) => schedule && !SETTLED.test(String(schedule.status ?? '')))
    .map((schedule) => ({
      at: scheduleMoment(schedule.scheduleDate, schedule.scheduleTime, schedule.scheduleTimeZone),
      timeZone: (schedule.scheduleTimeZone as string) || null,
    }))
    .filter((item): item is { at: Date; timeZone: string | null } => !!item.at && item.at.getTime() > now)
    .sort((a, b) => a.at.getTime() - b.at.getTime());

  return upcoming.length
    ? { at: upcoming[0].at, upcoming: upcoming.length, timeZone: upcoming[0].timeZone }
    : NO_NEXT;
}
